// [KEEP2] Client-side serialization for Oracle pipeline runs.
//
// A single full Oracle run fans out across ~25-30 /api/gemini POSTs. Launching N
// of them at once (e.g. one browser session assessing N holdings) blows the
// server-side per-user Gemini budget and makes every run fail mid-pipeline. This
// module runs at most ONE analyzeCompany() at a time per browser session, FIFO,
// and dedupes by ticker (a second request for an in-flight ticker gets the same
// promise instead of a duplicate run). Queue position is reported via onProgress
// so a waiting row shows why it is idle instead of a bare spinner.
//
// Control flow only — nothing here touches what the LLM sees.

type ProgressFn = (message: string) => void;

interface Waiter {
  ticker: string;
  onProgress?: ProgressFn;
}

const inFlight = new Map<string, Promise<unknown>>();
const waiters: Waiter[] = [];
let activeTicker: string | null = null;
// Serialization chain: each enqueued task runs after the previous one settles
// (success OR failure), so one failing run never stalls the queue.
let chain: Promise<unknown> = Promise.resolve();

function reportPositions(): void {
  waiters.forEach((w, idx) => {
    const ahead = idx + (activeTicker ? 1 : 0);
    if (ahead <= 0) return;
    w.onProgress?.(
      `Queued — ${ahead} ${ahead === 1 ? 'analysis' : 'analyses'} ahead of this one`
    );
  });
}

/**
 * Enqueue an Oracle analysis task. At most one runs at a time; others wait FIFO.
 * If `ticker` is already running or queued, the existing in-flight promise is
 * returned (dedupe) and `task` is not run again.
 */
export function enqueueAnalysis<T>(
  ticker: string,
  task: () => Promise<T>,
  onProgress?: ProgressFn
): Promise<T> {
  const key = ticker.toUpperCase();

  const existing = inFlight.get(key);
  if (existing) {
    onProgress?.('Analysis already in progress for this position…');
    return existing as Promise<T>;
  }

  const waiter: Waiter = { ticker: key, onProgress };
  waiters.push(waiter);
  reportPositions();

  const result = chain.then(async () => {
    // Reached the front of the queue.
    const idx = waiters.indexOf(waiter);
    if (idx >= 0) waiters.splice(idx, 1);
    activeTicker = key;
    reportPositions();
    try {
      return await task();
    } finally {
      activeTicker = null;
      inFlight.delete(key);
    }
  });

  // Keep the chain alive regardless of this task's outcome.
  chain = result.then(() => undefined, () => undefined);
  inFlight.set(key, result);
  return result as Promise<T>;
}
